"use client";

import { useRef, memo, useCallback, useMemo, useState, useEffect, use } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Filter } from "@/lib/interfaces/cards";
import { slugify, transformForURL, buildURL } from "../utils/filterUtils";

interface FiltersBarProps {
  selectedFilterIds?: string[];
  selectedFilter?: string;
  getFilterValue?: (id: string) => string;
  removeFilter?: (id: string) => void;
  clearFilters?: () => void;
  setSelectedFilter?: (value: string) => void;
  updateSortOption?: (value: string) => void;
  filters?: Filter[];
  filtersPromise?: Promise<Filter[]>;
}

function FiltersBar({
  selectedFilterIds,
  selectedFilter = "Most Popular",
  getFilterValue,
  removeFilter,
  clearFilters,
  setSelectedFilter,
  updateSortOption,
  filters: initialFilters = [],
  filtersPromise
}: FiltersBarProps) {
  const filters = filtersPromise ? use(filtersPromise) : initialFilters;
  const activeFiltersRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftInitial = useRef(0);

  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Local state for sort fallback
  const [localSort, setLocalSort] = useState(selectedFilter);

  useEffect(() => {
    setLocalSort(selectedFilter);
  }, [selectedFilter]);

  const activeSort = selectedFilter !== undefined ? selectedFilter : localSort;

  // Build maps locally from filters array to retrieve value and groupSlug
  const flat = useMemo(() => {
    return filters.flatMap((group) => {
      const gslug = slugify(group.filter_name);
      return group.values.map((v) => ({
        id: v.id,
        value: v.value,
        groupSlug: gslug,
      }));
    });
  }, [filters]);


  const idToValue = useMemo(() => Object.fromEntries(flat.map((o) => [o.id.toString(), o.value])), [flat]);
  const idToGroupSlug = useMemo(() => Object.fromEntries(flat.map((o) => [o.id.toString(), o.groupSlug])), [flat]);

  const groupToValueToId = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    for (const o of flat) {
      map[o.groupSlug] ||= {};
      map[o.groupSlug][transformForURL(o.value)] = o.id.toString();
    }
    return map;
  }, [flat]);

  // Derived filter IDs from URL searchParams (fallback if prop not provided)
  const resolvedSelectedFilterIds = useMemo(() => {
    // Explicitly stringify if provided as numbers or mixed
    const inputIds = selectedFilterIds !== undefined ? selectedFilterIds.map(x => x.toString()) : undefined;
    if (inputIds !== undefined) return inputIds;

    const ids: string[] = [];
    const groups = Array.from(new Set(flat.map((o) => o.groupSlug)));
    for (const gslug of groups) {
      const rawValue = searchParams.get(gslug);
      if (rawValue) {
        const values = rawValue.split(",");
        const v2id = groupToValueToId[gslug] || {};
        for (const value of values) {
          const id = v2id[value];
          if (id) ids.push(id.toString());
        }
      }
    }
    return ids;
  }, [selectedFilterIds, searchParams, groupToValueToId, flat]);

  // Allow horizontal scrolling with mouse wheel on desktop
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (activeFiltersRef.current) {
      if (e.deltaY !== 0) {
        activeFiltersRef.current.scrollLeft += e.deltaY;
      }
    }
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!activeFiltersRef.current) return;
    isDragging.current = true;
    startX.current = e.pageX - activeFiltersRef.current.offsetLeft;
    scrollLeftInitial.current = activeFiltersRef.current.scrollLeft;
    activeFiltersRef.current.style.cursor = 'grabbing';
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    if (activeFiltersRef.current) activeFiltersRef.current.style.cursor = 'grab';
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (activeFiltersRef.current) activeFiltersRef.current.style.cursor = 'grab';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !activeFiltersRef.current) return;
    e.preventDefault();
    const x = e.pageX - activeFiltersRef.current.offsetLeft;
    const walk = (x - startX.current) * 2; // speed multiplier
    activeFiltersRef.current.scrollLeft = scrollLeftInitial.current - walk;
  };

  const handleClearFilters = () => {
    if (clearFilters) {
      clearFilters();
      return;
    }
    // Standalone fallback: Clear all filter search params but keep sort
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.delete("page"); // new filter or sort starts again at page 1
    const groups = Array.from(new Set(flat.map((o) => o.groupSlug)));
    for (const gslug of groups) {
      currentParams.delete(gslug);
    }
    router.replace(buildURL(pathname, currentParams), { scroll: false });
  };

  const handleRemoveFilter = (filterId: string) => {
    if (removeFilter) {
      removeFilter(filterId);
      return;
    }

    // Standalone fallback
    const groupSlug = idToGroupSlug[filterId];
    const filterValue = idToValue[filterId];
    if (!groupSlug || !filterValue) return;

    const currentParams = new URLSearchParams(searchParams.toString());

    currentParams.delete("page"); // new filter or sort starts again at page 1
    const currentValues = (currentParams.get(groupSlug) || "").split(",");
    const transformedValue = transformForURL(filterValue);
    const newValues = currentValues.filter((v) => v !== transformedValue);

    if (newValues.length === 0) currentParams.delete(groupSlug);
    else currentParams.set(groupSlug, newValues.join(","));

    router.replace(buildURL(pathname, currentParams), { scroll: false });
  };

  const handleSortChange = (value: string) => {
    if (setSelectedFilter) setSelectedFilter(value);
    if (updateSortOption) {
      updateSortOption(value);
      return;
    }

    // Standalone fallback
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.delete("page"); // new filter or sort starts again at page 1
    currentParams.set("sort_by", value);
    router.replace(buildURL(pathname, currentParams), { scroll: false });
  };

  const getFilterValueFn = (id: string) => {
    if (getFilterValue) return getFilterValue(id);
    return idToValue[id] || id;
  };

  return (
    <div className="flex min-w-0 max-w-full items-center">
      <div className="flex min-w-0 flex-nowrap items-center justify-end gap-3">
        <div
          className={`${resolvedSelectedFilterIds.length > 0 ? "flex" : "hidden"} overflow-x-auto sm:space-x-4 space-x-2 gap-1 sm:gap-2 select-none pb-1 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] cursor-grab active:cursor-grabbing`}
          ref={activeFiltersRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          style={{ maxWidth: "min(60vw, 520px)" }}
        >
          {resolvedSelectedFilterIds.length > 0 && (
            <button
              type="button"
              className="px-3 py-1 flex-shrink-0 w-auto rounded-full border border-paper-rule bg-white text-xs font-semibold text-ink flex items-center cursor-pointer hover:bg-paper whitespace-nowrap"
              onClick={handleClearFilters}
            >
              CLEAR ALL
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 ml-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {resolvedSelectedFilterIds.map((filter) => (
            <div
              key={filter}
              className="px-3 py-1 flex-shrink-0 w-auto bg-ink rounded-full text-xs sm:text-sm flex items-center cursor-pointer hover:bg-ink-700 whitespace-nowrap text-white"
            >
              {getFilterValueFn(filter)}
              <button
                type="button"
                className="ml-2 rounded-full"
                aria-label={`Remove filter ${getFilterValueFn(filter)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFilter(filter);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
        <div className="flex-shrink-0">
          <select
            className="form-select h-9 rounded-full border border-paper-rule bg-paper pl-4 pr-9 text-sm font-medium text-ink cursor-pointer focus:outline-none focus:ring-2 focus:ring-ink"
            value={activeSort}
            id="sort_by_select"
            name="sort_by_select"
            aria-label="Sort cards by"
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option>Most Popular</option>
            <option>Staff Picks</option>
            <option>Newest</option>
          </select>
        </div>
      </div>
    </div>
  );
}

export default memo(FiltersBar);